import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  CheckCircle,
  DownloadSimple,
  EnvelopeSimple,
  Hourglass,
  LinkBreak,
  PhoneCall,
  UserCircle,
  WarningCircle,
} from "@phosphor-icons/react";

import { publicShare } from "@/api";
import { ReadOnlyDocumentViewer } from "@/components/ReadOnlyDocumentViewer";
import PublicShareRightExchangeV2 from "@/components/PublicShareRightExchangeV2";
import PublicInstallationDeliveryCard from "@/components/gmao/PublicInstallationDeliveryCard";
import SignaturePad from "@/components/gmao/SignaturePad";
import { formatApiError, publicFileUrl } from "@/lib/api";


const HEARTBEAT_MS = 10000;
const API = process.env.REACT_APP_BACKEND_URL;


export default function PublicShare() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [activeDoc, setActiveDoc] = useState(null);
  const [signatureBusy, setSignatureBusy] = useState(false);
  const [signatureConsent, setSignatureConsent] = useState(false);
  const [signatureError, setSignatureError] = useState("");
  const tickRef = useRef(null);

  useEffect(() => {
    publicShare.fetchInfo(token)
      .then((infoData) => {
        setInfo(infoData);
        if (infoData.status === "active") {
          return publicShare.fetchShare(token).then((shareData) => {
            setData(shareData);
            if (shareData.documents?.length) setActiveDoc(shareData.documents[0]);
          });
        }
        return null;
      })
      .catch((error) => {
        setErr(formatApiError(error.response?.data?.detail) || error.message);
      });
  }, [token]);

  useEffect(() => {
    if (!activeDoc) return undefined;
    const tick = async () => {
      try {
        await publicShare.tickReadingTime(token, activeDoc.id, 10);
      } catch {}
    };
    tickRef.current = setInterval(tick, HEARTBEAT_MS);
    return () => clearInterval(tickRef.current);
  }, [activeDoc, token]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--do-bg-solid)]" data-testid="public-share-error">
        <div className="max-w-md text-center">
          <div className="label-eyebrow mb-2">Lien indisponible</div>
          <h1 className="font-heading text-2xl font-black tracking-tight">{err}</h1>
        </div>
      </div>
    );
  }

  if (!info) {
    return <div className="min-h-screen flex items-center justify-center"><div className="label-eyebrow lirah-pulse">Chargement</div></div>;
  }

  if (info.status !== "active") {
    return <ReopenScreen info={info} token={token} />;
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center"><div className="label-eyebrow lirah-pulse">Chargement</div></div>;
  }

  const signReport = async (file, customerName) => {
    if (!signatureConsent || signatureBusy) {
      setSignatureError("Veuillez confirmer votre accord avant de signer.");
      return;
    }
    setSignatureError("");
    setSignatureBusy(true);
    try {
      await publicShare.signInstallationReport(token, file, customerName);
      const fresh = await publicShare.fetchShare(token);
      setData(fresh);
      setActiveDoc(fresh.documents?.[0] || null);
    } catch (err) {
      setSignatureError(formatApiError(err.response?.data?.detail) || "Signature impossible. Veuillez réessayer.");
    } finally {
      setSignatureBusy(false);
    }
  };

  const fileUrl = activeDoc ? publicFileUrl(token, activeDoc.id) : "";
  const ext = (activeDoc?.extension || "").toLowerCase();
  const isImage = ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(ext);
  const isPdf = ext === "pdf";
  const isHtml = ["html", "htm"].includes(ext);
  const isOffice = ["xls", "xlsx", "doc", "docx", "ppt", "pptx"].includes(ext);
  const isStaticDocument = isPdf || isOffice || isImage;

  return (
    <div
      className="min-h-screen bg-[var(--do-bg-2)] flex flex-col"
      data-testid="public-share"
      onContextMenu={(event) => {
        if (!info?.allowDownload) event.preventDefault();
      }}
    >
      <header className="relative h-16 shrink-0 border-b border-[var(--do-border-strong)] bg-[var(--do-ink)] px-4 text-[var(--do-on-accent)] sm:px-8">
        <div className="flex h-full items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            {info?.tenantLogoUrl ? (
              <img
                src={`${API}${info.tenantLogoUrl}`}
                alt={info.tenantName || "Logo entreprise"}
                className="h-10 w-10 rounded-lg border border-[var(--do-border)] bg-[var(--do-bg-solid)] object-contain"
              />
            ) : (
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[var(--do-accent)] font-black">A</div>
            )}
            <div className="truncate font-heading text-sm font-black uppercase tracking-[0.14em] sm:text-base">
              {info?.tenantName || data.tenantName || "Entreprise"}
            </div>
          </div>

          <div className="hidden items-center gap-3 xl:flex">
            <UserCircle size={24} />
            <div className="min-w-0">
              <div className="truncate text-sm font-black">
                {info?.sender?.name || data?.createdByName || info?.createdByName || "Votre conseiller"}
              </div>
              <div className="mt-0.5 flex gap-3 text-xs opacity-80">
                <span className="inline-flex items-center gap-1"><PhoneCall size={13} />{info?.sender?.phone || "Téléphone non renseigné"}</span>
                <span className="inline-flex items-center gap-1"><EnvelopeSimple size={13} />{info?.sender?.email || "Email non renseigné"}</span>
              </div>
            </div>
          </div>

          <div className="pointer-events-none absolute left-1/2 hidden max-w-[38vw] -translate-x-1/2 truncate font-heading text-sm font-bold md:block sm:text-base">
            {activeDoc?.name || "Document"}
          </div>
        </div>
      </header>

      <PublicInstallationDeliveryCard token={token} />

      <div className="flex min-h-0 flex-1">
        <PublicShareRightExchangeV2
          token={token}
          senderName={info?.sender?.name || data?.createdByName || "votre contact"}
        />

        <main className="flex min-w-0 flex-1 flex-col bg-[var(--do-bg-solid)]" data-testid="public-viewer">
          {data.documents?.length > 1 && (
            <nav
              aria-label="Documents partagés"
              className="flex shrink-0 gap-2 overflow-x-auto border-b border-[var(--do-border)] bg-[var(--do-bg-2)] px-3 py-3 sm:px-5"
              data-testid="public-share-document-tabs"
            >
              {data.documents.map((document) => (
                <button
                  key={document.id}
                  type="button"
                  onClick={() => setActiveDoc(document)}
                  aria-current={activeDoc?.id === document.id ? "page" : undefined}
                  className={`max-w-[min(75vw,320px)] shrink-0 truncate rounded-full border px-4 py-2 text-sm font-semibold transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--do-accent)] ${
                    activeDoc?.id === document.id
                      ? "border-[var(--do-accent)] bg-[var(--do-accent)] text-[var(--do-on-accent)]"
                      : "border-[var(--do-border)] bg-[var(--do-bg-solid)] text-[var(--do-ink)] hover:bg-[var(--do-accent-soft)]"
                  }`}
                  title={document.name}
                >
                  {document.name}
                </button>
              ))}
            </nav>
          )}
          {activeDoc ? (
            <>
              {info?.allowDownload && (
                <div className="flex h-10 shrink-0 items-center justify-end border-b border-[var(--do-border)] px-5">
                  <a
                    href={`${publicFileUrl(token, activeDoc.id)}?download=true`}
                    download={activeDoc.name}
                    className="inline-flex items-center gap-1 rounded-md border border-[var(--do-border)] px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-[var(--do-accent)]"
                  >
                    <DownloadSimple size={12} weight="bold" /> Télécharger
                  </a>
                </div>
              )}

              <div className="relative flex-1 overflow-hidden" style={{ userSelect: info?.allowDownload ? "auto" : "none" }}>
                {isStaticDocument && (
                  <ReadOnlyDocumentViewer
                    token={token}
                    documentId={activeDoc.id}
                    documentName={activeDoc.name}
                  />
                )}
                {isHtml && (
                  <iframe
                    src={fileUrl}
                    title={activeDoc.name}
                    className="h-full w-full bg-[var(--do-bg-solid)]"
                    sandbox="allow-scripts allow-same-origin allow-forms"
                  />
                )}
                {!isStaticDocument && !isHtml && (
                  <div className="flex h-full items-center justify-center text-sm text-[var(--do-muted)]">
                    Aperçu non disponible pour ce type de fichier.
                  </div>
                )}
                {info?.watermarkEnabled && (
                  <Watermark
                    text={`${data.prospectFirstName || ""} ${data.prospectName || info.prospectName || ""} · ${new Date().toLocaleString("fr-FR")}`}
                  />
                )}
              </div>
              {data.installationSignatureRequired && !data.installationSignedAt && isPdf && (
                <section className="shrink-0 border-t border-[var(--do-border)] bg-[var(--do-bg-solid)] p-4"
                  data-testid="public-installation-signature">
                  <h2 className="mb-2 font-heading text-lg font-bold">Signer le bon d'intervention affiché</h2>
                  <p className="mb-3 text-sm text-[var(--do-muted)]">La signature sera apposée sur une copie PDF archivée de ce document.</p>
                  <label className="mb-3 flex items-start gap-2 text-sm">
                    <input type="checkbox" checked={signatureConsent}
                      onChange={(event) => setSignatureConsent(event.target.checked)} />
                    Je confirme avoir lu le bon d'intervention et souhaite y apposer ma signature.
                  </label>
                  {signatureError && <p role="alert" className="mb-2 text-sm text-red-600">{signatureError}</p>}
                  <div className="max-h-[300px] overflow-auto">
                    <SignaturePad editable={signatureConsent} saving={signatureBusy}
                      onSave={signReport} />
                  </div>
                </section>
              )}
              {data.installationSignatureRequired && data.installationSignedAt && (
                <p className="border-t border-[var(--do-border)] p-4 text-sm font-semibold text-[var(--do-accent)]"
                  data-testid="public-installation-signed">
                  Bon d'intervention signé. La version signée est maintenant disponible.
                </p>
              )}
            </>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-[var(--do-muted)]">Sélectionnez un document.</div>
          )}
        </main>
      </div>

      <footer className="flex h-9 shrink-0 items-center justify-center border-t border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 text-[11px] text-[var(--do-muted)]">
        <a href="/legal/confidentialite" target="_blank" rel="noopener noreferrer" className="font-semibold text-[var(--do-accent)] underline">
          Politique de confidentialité
        </a>
      </footer>
    </div>
  );
}


function Watermark({ text }) {
  const stripe = `<svg xmlns='http://www.w3.org/2000/svg' width='560' height='180'><text x='10' y='110' transform='rotate(-22 280 90)' fill='rgba(15,23,42,0.10)' font-family='system-ui,sans-serif' font-weight='700' font-size='14' letter-spacing='1'>${text.replace(/&/g, "&amp;").replace(/</g, "&lt;").slice(0, 200)}</text></svg>`;
  const url = `url("data:image/svg+xml;utf8,${encodeURIComponent(stripe)}")`;
  return (
    <div
      className="pointer-events-none absolute inset-0 z-10"
      style={{ backgroundImage: url, backgroundRepeat: "repeat", backgroundSize: "560px 180px", mixBlendMode: "multiply" }}
    />
  );
}


const STATUS_COPY = {
  expired: { title: "Ce lien a expiré", icon: Hourglass },
  exhausted: { title: "Limite d'ouvertures atteinte", icon: LinkBreak },
  revoked: { title: "Ce lien a été révoqué", icon: WarningCircle },
};


function ReopenScreen({ info, token }) {
  const meta = STATUS_COPY[info.status] || STATUS_COPY.expired;
  const Icon = meta.icon;
  const [message, setMessage] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      const result = await publicShare.requestReopen(token, {
        message,
        contact_phone: contactPhone,
        contact_email: contactEmail,
      });
      setDone(result);
    } catch (error) {
      setDone({ error: formatApiError(error.response?.data?.detail) || error.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen lirah-gradient-hero flex items-center justify-center p-6" data-testid="public-share-reopen">
      <div className="w-full max-w-lg rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] p-8 shadow-xl">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--do-accent-soft)] text-[var(--do-accent)]">
            <Icon size={24} weight="duotone" />
          </div>
          <div>
            <div className="label-eyebrow">{info.tenantName || "Partage sécurisé"}</div>
            <h1 className="mt-1 font-heading text-2xl font-black">{meta.title}</h1>
            <p className="mt-2 text-sm text-[var(--do-muted)]">Vous pouvez demander un nouvel accès à l’expéditeur.</p>
          </div>
        </div>

        {done?.ok ? (
          <div className="mt-5 flex items-start gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] p-4 text-sm font-semibold">
            <CheckCircle size={19} weight="fill" className="text-[var(--do-accent)]" />
            Demande envoyée.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-6 grid gap-3">
            <input
              value={contactPhone}
              onChange={(event) => setContactPhone(event.target.value)}
              placeholder="Téléphone"
              className="h-11 rounded-xl border border-[var(--do-border)] px-3 text-sm outline-none focus:border-[var(--do-accent)]"
            />
            <input
              type="email"
              value={contactEmail}
              onChange={(event) => setContactEmail(event.target.value)}
              placeholder="E-mail"
              className="h-11 rounded-xl border border-[var(--do-border)] px-3 text-sm outline-none focus:border-[var(--do-accent)]"
            />
            <textarea
              rows={4}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Votre message"
              className="rounded-xl border border-[var(--do-border)] p-3 text-sm outline-none focus:border-[var(--do-accent)]"
            />
            {done?.error && <p className="text-sm font-semibold text-red-600">{done.error}</p>}
            <button
              type="submit"
              disabled={submitting}
              className="h-11 rounded-xl bg-[var(--do-accent)] px-4 text-sm font-black text-[var(--do-on-accent)] disabled:opacity-50"
            >
              {submitting ? "Envoi…" : "Demander un nouvel accès"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
